import React, { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

const BASE = "https://ltwph7gpvb.execute-api.us-east-1.amazonaws.com";
const CHARTER_TYPES = ["Time Charter", "Contract (COA)"];
const VESSEL_TYPES = ["VLGC", "Aframax", "Supramax", "MR Tanker"];

async function authHeaders() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

const openBadge = (days) =>
  days <= 0
    ? <span className="badge low">Open now</span>
    : <span className={`badge ${days <= 14 ? "medium" : "other"}`}>Opens in {days} days</span>;

function CharterTable({ rows, showBallast }) {
  return (
    <div className="tablewrap">
      <table>
        <thead>
          <tr>
            {showBallast && <th>#</th>}
            <th>Vessel</th><th>Type</th><th>Charter</th><th>Open at</th><th>Availability</th>
            {showBallast && <th>Ballast to load port</th>}
            <th>Period</th><th>Rate</th><th>Contact</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c, i) => (
            <tr key={c.charterId}>
              {showBallast && <td>{i + 1}</td>}
              <td><b>{c.vesselName}</b></td>
              <td>{c.vesselType}</td>
              <td>{c.charterType}</td>
              <td>{c.openPort}<br /><span className="note">{c.openDate}</span></td>
              <td>{openBadge(c.opensInDays)}</td>
              {showBallast && (
                <td>{c.ballastNm === null ? "n/a" : `${c.ballastNm.toLocaleString()} nm · ${c.ballastDays} days`}</td>
              )}
              <td>{c.period}</td>
              <td>{c.rate}</td>
              <td><a href={`mailto:${c.contactEmail}`}>{c.contactName}</a></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Chartering() {
  const [charters, setCharters] = useState([]);
  const [charterType, setCharterType] = useState("");
  const [vesselType, setVesselType] = useState("");
  const [ports, setPorts] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [port, setPort] = useState("Mundra");
  const [cargo, setCargo] = useState("LPG");
  const [match, setMatch] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const q = new URLSearchParams();
        if (charterType) q.set("charterType", charterType);
        if (vesselType) q.set("vesselType", vesselType);
        const res = await fetch(`${BASE}/charters?${q}`, { headers: await authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
        setCharters(data);
      } catch (err) {
        setError(err.message);
      }
    })();
  }, [charterType, vesselType]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE}/ports`, { headers: await authHeaders() });
        const data = await res.json();
        setPorts(data.ports);
        setCargos(data.cargos);
      } catch {
        setError("Could not load ports");
      }
    })();
  }, []);

  async function findMatch() {
    setError("");
    setMatch(null);
    try {
      const res = await fetch(`${BASE}/charters/match`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({ port, cargo })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      setMatch(data);
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div>
      {error && <div className="err">{error}</div>}

      <div className="card">
        <h2>Vessel matcher</h2>
        <div className="form">
          <div>
            <label>Load port</label>
            <select value={port} onChange={(e) => setPort(e.target.value)}>
              {ports.map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label>Cargo</label>
            <select value={cargo} onChange={(e) => setCargo(e.target.value)}>
              {cargos.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <button className="btn" onClick={findMatch}>Find vessels</button>
        {match && (
          <div style={{ marginTop: 16 }}>
            <p className="note">
              {cargo} needs a <b>{match.vesselType}</b>. Ranked by ballast distance to {port}.
            </p>
            {match.results.length
              ? <CharterTable rows={match.results} showBallast />
              : <p className="note">No suitable open vessel found.</p>}
          </div>
        )}
      </div>

      <div className="card">
        <h2>Open vessels</h2>
        <div className="form">
          <div>
            <label>Charter type</label>
            <select value={charterType} onChange={(e) => setCharterType(e.target.value)}>
              <option value="">All</option>
              {CHARTER_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label>Vessel type</label>
            <select value={vesselType} onChange={(e) => setVesselType(e.target.value)}>
              <option value="">All</option>
              {VESSEL_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <CharterTable rows={charters} />
      </div>
    </div>
  );
}